/**
 * TokenPricingIndex.js  —  v34.94 (Feb 13 2026)
 *
 * Public LLM pricing index — designed to be the de-facto Wikipedia of
 * AI token prices. Lives at /tokens (future subdomain: tokens.cartieai.com).
 *
 * Sections (top to bottom, matching agreed spec):
 *  1. Hero + freshness pill
 *  2. The Index — sortable table of ~24 models across 8 providers
 *  3. Cost Calculator — sliders + side-by-side cost comparison
 *  4. Hidden Costs — 4 cards (cache · fine-tune · image/audio · context)
 *  5. Cheapest-per-tier scoreboard
 *  6. Free public JSON API docs (citation-friendly)
 *  7. Soft CTA — "want this on YOUR usage?"
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, ArrowDown, ArrowUp, Sparkles, Copy, Check } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicNavbar from '../components/PublicNavbar';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  frontier:  { label: 'Frontier',  cls: 'bg-violet-500/15 text-violet-300 ring-violet-500/30' },
  reasoning: { label: 'Reasoning', cls: 'bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30' },
  balanced:  { label: 'Balanced',  cls: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30' },
  cheap:     { label: 'Cheap',     cls: 'bg-cyan-500/15 text-cyan-300 ring-cyan-500/30' },
};

const PROVIDER_COLORS = {
  OpenAI:    'text-emerald-300',
  Anthropic: 'text-orange-300',
  Google:    'text-sky-300',
  Meta:      'text-blue-300',
  DeepSeek:  'text-pink-300',
  Mistral:   'text-amber-300',
  xAI:       'text-slate-200',
  Cohere:    'text-rose-300',
  AWS:       'text-yellow-300',
};

const fmtUSD = (n) => {
  if (n === null || n === undefined) return '—';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1)    return `$${n.toFixed(3)}`;
  if (n < 10)   return `$${n.toFixed(2)}`;
  return `$${n.toFixed(2)}`;
};

const fmtUSDLarge = (n) => {
  if (n === null || n === undefined) return '$0';
  if (n < 1) return `$${n.toFixed(4)}`;
  if (n < 1000) return `$${n.toFixed(2)}`;
  if (n < 1_000_000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${(n / 1_000_000).toFixed(2)}M`;
};

// =============================================================================
// THE INDEX — sortable table
// =============================================================================
const SortIcon = ({ active, dir }) => {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3 text-emerald-300" /> : <ArrowDown className="w-3 h-3 text-emerald-300" />;
};

const Th = ({ k, sort, onCycle, children, align = 'left' }) => (
  <th
    onClick={() => onCycle(k)}
    className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 cursor-pointer select-none hover:text-white transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    <span className={`inline-flex items-center gap-1.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      {children}
      <SortIcon active={sort.key === k} dir={sort.dir} />
    </span>
  </th>
);

const PriceIndexTable = ({ models }) => {
  const [sort, setSort] = useState({ key: 'input', dir: 'asc' });

  const sorted = useMemo(() => {
    const arr = [...models];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [models, sort]);

  const cycle = (key) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }
    ));
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="token-pricing-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] border-b border-white/10">
            <tr>
              <Th k="provider" sort={sort} onCycle={cycle}>Provider</Th>
              <Th k="display_name" sort={sort} onCycle={cycle}>Model</Th>
              <Th k="input" sort={sort} onCycle={cycle} align="right">$/1M in</Th>
              <Th k="output" sort={sort} onCycle={cycle} align="right">$/1M out</Th>
              <Th k="context_k" sort={sort} onCycle={cycle} align="right">Context</Th>
              <Th k="tier" sort={sort} onCycle={cycle}>Tier</Th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400 text-left">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {sorted.map((m) => {
              const t = TIER_STYLES[m.tier] || TIER_STYLES.balanced;
              const pc = PROVIDER_COLORS[m.provider] || 'text-slate-200';
              return (
                <tr
                  key={m.model}
                  className="hover:bg-white/[0.03] transition-colors"
                  data-testid={`token-row-${m.model}`}
                >
                  <td className={`px-3 py-3 font-semibold ${pc}`}>{m.provider}</td>
                  <td className="px-3 py-3 text-white">
                    <div className="font-medium">{m.display_name}</div>
                    <div className="text-[11px] font-mono text-slate-500">{m.model}</div>
                  </td>
                  <td className="px-3 py-3 text-right text-slate-100 tabular-nums">{fmtUSD(m.input)}</td>
                  <td className="px-3 py-3 text-right text-slate-100 tabular-nums">{fmtUSD(m.output)}</td>
                  <td className="px-3 py-3 text-right text-slate-300 tabular-nums">{m.context_k >= 1000 ? `${m.context_k / 1000}M` : `${m.context_k}k`}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${t.cls}`}>{t.label}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-400 max-w-[260px]">{m.notes}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// COST CALCULATOR
// =============================================================================
const CostCalculator = ({ models }) => {
  const [reqs, setReqs] = useState(100_000);     // requests/month
  const [inTok, setInTok] = useState(800);       // avg input tokens
  const [outTok, setOutTok] = useState(400);     // avg output tokens

  const results = useMemo(() => {
    const inM = (reqs * inTok) / 1_000_000;
    const outM = (reqs * outTok) / 1_000_000;
    const list = models.map((m) => ({
      ...m,
      monthly: inM * m.input + outM * m.output,
    }));
    list.sort((a, b) => a.monthly - b.monthly);
    return list;
  }, [models, reqs, inTok, outTok]);

  const cheapest = results[0];
  const mostExpensive = results[results.length - 1];
  const savings = mostExpensive && cheapest ? mostExpensive.monthly - cheapest.monthly : 0;

  return (
    <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-950/80 p-6 sm:p-8" data-testid="token-cost-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <h3 className="text-xl font-bold text-white">Cost Calculator</h3>
      </div>
      <p className="text-sm text-slate-400 mb-6">Estimate your monthly bill across every major model.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <NumberInput label="Requests / month" value={reqs} setValue={setReqs} step={10_000} min={1_000} max={100_000_000} testid="calc-requests" />
        <NumberInput label="Avg input tokens" value={inTok} setValue={setInTok} step={100} min={1} max={1_000_000} testid="calc-input-tokens" />
        <NumberInput label="Avg output tokens" value={outTok} setValue={setOutTok} step={100} min={1} max={1_000_000} testid="calc-output-tokens" />
      </div>

      {/* Spread headline */}
      <div className="mb-5 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
        <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-1">Spread</p>
        <p className="text-white text-sm sm:text-base">
          Cheapest <span className="font-bold text-emerald-300">{cheapest?.display_name}</span> at{' '}
          <span className="font-mono">{fmtUSDLarge(cheapest?.monthly)}/mo</span> · most expensive{' '}
          <span className="font-bold text-rose-300">{mostExpensive?.display_name}</span> at{' '}
          <span className="font-mono">{fmtUSDLarge(mostExpensive?.monthly)}/mo</span> · spread is{' '}
          <span className="font-bold text-emerald-300">{fmtUSDLarge(savings)}/mo</span>.
        </p>
      </div>

      {/* Top 8 results */}
      <div className="space-y-1.5" data-testid="calc-results">
        {results.slice(0, 8).map((m, i) => {
          const t = TIER_STYLES[m.tier] || TIER_STYLES.balanced;
          const pc = PROVIDER_COLORS[m.provider] || 'text-slate-200';
          const isCheapest = i === 0;
          return (
            <div
              key={m.model}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${isCheapest ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/[0.02] border-white/5'}`}
            >
              <span className="text-xs font-mono text-slate-500 w-6 text-right">{i + 1}.</span>
              <span className={`text-sm font-semibold ${pc} w-20`}>{m.provider}</span>
              <span className="text-sm text-white flex-1 truncate">{m.display_name}</span>
              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ${t.cls}`}>{t.label}</span>
              <span className="text-sm font-mono text-slate-100 tabular-nums w-24 text-right">{fmtUSDLarge(m.monthly)}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-4 text-center">
        Math = (requests × input_tokens / 1M × $input) + (requests × output_tokens / 1M × $output). Excludes cache discounts, fine-tuning, multimodal, and committed-use rates.
      </p>
    </section>
  );
};

const NumberInput = ({ label, value, setValue, step, min, max, testid }) => (
  <label className="block">
    <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1.5 block">{label}</span>
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => setValue(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
      className="w-full px-3 py-2 bg-slate-950/70 border border-white/10 rounded-lg text-white tabular-nums focus:border-emerald-500/40 focus:outline-none transition-colors"
      data-testid={testid}
    />
  </label>
);

// =============================================================================
// HIDDEN COSTS
// =============================================================================
const HIDDEN_COSTS = [
  {
    title: 'Cache pricing diff',
    body: 'Anthropic & OpenAI offer 90% discount on cache READS but 25% premium on cache WRITES. Get this wrong and you pay 3× more.',
    accent: 'emerald',
  },
  {
    title: 'Fine-tuning surcharge',
    body: 'Fine-tuned models cost 4-6× the base model per token. Sometimes a smaller base + good prompts is cheaper.',
    accent: 'cyan',
  },
  {
    title: 'Image/audio token math',
    body: 'A single image can be 1,000-12,000 tokens depending on detail tier. Audio billed per second, not per token.',
    accent: 'violet',
  },
  {
    title: 'Context-window amplifier',
    body: 'Filling a 1M-token context with Gemini = $2 per request even with cheap pricing. Use the smallest window you can.',
    accent: 'amber',
  },
];

const ACCENT_RING = {
  emerald: 'ring-emerald-500/30 bg-emerald-500/10',
  cyan: 'ring-cyan-500/30 bg-cyan-500/10',
  violet: 'ring-violet-500/30 bg-violet-500/10',
  amber: 'ring-amber-500/30 bg-amber-500/10',
};

const HiddenCosts = () => (
  <section data-testid="token-hidden-costs">
    <h3 className="text-xl font-bold text-white mb-1">Hidden costs nobody talks about</h3>
    <p className="text-sm text-slate-400 mb-6">Provider pricing pages don&apos;t advertise these — but they&apos;ll wreck your monthly bill.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {HIDDEN_COSTS.map((c) => (
        <div key={c.title} className={`p-5 rounded-2xl border border-white/10 bg-white/[0.02]`}>
          <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ring-1 ${ACCENT_RING[c.accent]} mb-3`}>
            <span className="w-2 h-2 rounded-full bg-current opacity-70" />
          </div>
          <h4 className="font-semibold text-white mb-1.5">{c.title}</h4>
          <p className="text-sm text-slate-400 leading-relaxed">{c.body}</p>
        </div>
      ))}
    </div>
  </section>
);

// =============================================================================
// CHEAPEST-PER-TIER SCOREBOARD
// =============================================================================
const Scoreboard = ({ models }) => {
  const cheapestByTier = useMemo(() => {
    const map = {};
    for (const m of models) {
      const blended = m.input + m.output; // 1:1 blend; simple, fair
      if (!map[m.tier] || blended < map[m.tier]._blended) {
        map[m.tier] = { ...m, _blended: blended };
      }
    }
    return ['frontier', 'reasoning', 'balanced', 'cheap'].map((t) => map[t]).filter(Boolean);
  }, [models]);

  return (
    <section data-testid="token-scoreboard">
      <h3 className="text-xl font-bold text-white mb-1">Cheapest per tier</h3>
      <p className="text-sm text-slate-400 mb-6">Best dollar-for-dollar pick in each quality band (blended in/out).</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cheapestByTier.map((m) => {
          const t = TIER_STYLES[m.tier];
          const pc = PROVIDER_COLORS[m.provider] || 'text-slate-200';
          return (
            <div key={m.tier} className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${t.cls} mb-3`}>{t.label}</span>
              <div className="text-white font-semibold mb-1">{m.display_name}</div>
              <div className={`text-sm font-medium ${pc} mb-3`}>{m.provider}</div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-emerald-300 tabular-nums">{fmtUSD(m.input)}</span>
                <span className="text-xs text-slate-500">/ 1M in</span>
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-sm text-slate-200 tabular-nums">{fmtUSD(m.output)}</span>
                <span className="text-xs text-slate-500">/ 1M out</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// =============================================================================
// PUBLIC API DOCS
// =============================================================================
const ApiDocs = () => {
  const [copied, setCopied] = useState(false);
  const url = `${API}/api/v1/prices`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { /* ignore */ }
  };
  return (
    <section data-testid="token-api-docs">
      <h3 className="text-xl font-bold text-white mb-1">Free, citable JSON API</h3>
      <p className="text-sm text-slate-400 mb-5">Embed this in your tools. No key required. Updated weekly.</p>
      <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-5 font-mono text-sm">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="text-emerald-300">GET {url}</span>
          <button
            onClick={copy}
            className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 transition-colors"
            data-testid="token-api-copy"
          >
            {copied ? <><Check className="w-3 h-3 text-emerald-300" />Copied</> : <><Copy className="w-3 h-3" />Copy</>}
          </button>
        </div>
        <pre className="text-xs text-slate-300 overflow-x-auto leading-relaxed">{`{
  "powered_by": "CARTIEAI",
  "docs": "https://cartieai.com/tokens",
  "last_updated": "2026-02-13",
  "currency": "USD",
  "unit": "per_1m_tokens",
  "models": [
    { "provider": "OpenAI", "model": "gpt-5.2", "input": 1.75, "output": 14.00, "context_k": 400, "tier": "frontier", ... },
    ...
  ]
}`}</pre>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">Please cache responses for ≥1 hour. Cite CARTIEAI if you build with this data. Refreshed weekly.</p>
    </section>
  );
};

// =============================================================================
// SOFT CTA
// =============================================================================
const SoftCTA = () => (
  <section data-testid="token-soft-cta" className="text-center py-10">
    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Want this math on <span className="text-emerald-300">your</span> actual usage?</h3>
    <p className="text-slate-400 max-w-2xl mx-auto mb-6">CARTIE plugs into your LLM proxy and shows the same numbers — line by line, route by route, with model-swap recommendations.</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        to="/tools/ai-cost-calculator"
        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors"
        data-testid="token-cta-calculator"
      >
        Try the AI Cost Calculator
      </Link>
      <Link
        to="/signup"
        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition-colors"
        data-testid="token-cta-signup"
      >
        Start free — connect in 60 sec
      </Link>
    </div>
  </section>
);

// =============================================================================
// PAGE SHELL
// =============================================================================
export default function TokenPricingIndex() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/tokens/pricing`);
        if (!r.ok) throw new Error(`status ${r.status}`);
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.message || 'failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead
        title="LLM Token Pricing Index · CARTIEAI"
        description="Free, always-current pricing for every major LLM. Compare GPT-5.2, Claude 4.5, Gemini 3, Llama, DeepSeek, Mistral and more — with a live cost calculator and citable JSON API."
        path="/tokens"
      />
      <PublicNavbar />

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-20">
        {/* HERO */}
        <header className="mb-10" data-testid="token-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            <span className="text-[11px] text-emerald-200 font-medium tracking-wide">
              {data ? `Last verified ${data.last_verified}` : 'Live data'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            LLM Token <span className="text-emerald-300">Pricing Index</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl">
            Every major model, side by side. Free forever. Powered by CARTIEAI.
          </p>
        </header>

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 px-4 py-3 mb-6" data-testid="token-error">
            Couldn&apos;t load pricing data: {error}
          </div>
        )}

        {data && (
          <div className="space-y-12">
            <PriceIndexTable models={data.models} />
            <Scoreboard models={data.models} />
            <CostCalculator models={data.models} />
            <HiddenCosts />
            <ApiDocs />
            <SoftCTA />
          </div>
        )}

        {!data && !error && (
          <div className="space-y-4" data-testid="token-loading">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
